import { Archive, ArrowLeft, ArrowRightCircle, Pencil, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailColumns } from '@/components/ui/detail-columns';
import type { ApiError } from '@/core/api';
import { AuditTimelinePanel } from '@/features/audit';
import { CommentsPanel } from '@/features/comments';
import { ActivitiesPanel } from '@/features/crm/activities';
import { useCompany } from '@/features/crm/companies/queries';
import { useContact } from '@/features/crm/contacts/queries';
import { AttachmentsPanel } from '@/features/documents';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { TagsPanel } from '@/features/tags';
import { contactName } from '@/features/crm/contacts/types';
import { CompanyProfileFields } from '@/features/crm/companies/components/CompanyProfileFields';
import { sourceLabel, statusLabel, statusVariant } from '../constants';
import { IcpFitBadge } from '../components/IcpFitBadge';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { ConvertLeadDialog } from '../components/ConvertLeadDialog';
import { useArchiveLead, useConvertLead, useLead, usePurgeLead, useRestoreLead } from '../queries';

export function LeadDetailPage() {
  const { publicId = '' } = useParams();
  const leadId = publicId;
  const navigate = useNavigate();

  const { data: lead, isPending, isError, error } = useLead(leadId);
  const company = useCompany(lead?.company ?? -1);
  const contact = useContact(lead?.contact ?? -1);
  const archive = useArchiveLead();
  const restore = useRestoreLead();
  const purge = usePurgeLead();
  const convert = useConvertLead();
  const [convertOpen, setConvertOpen] = useState(false);

  async function handleConvert() {
    if (!lead) return;
    const deal = await convert.mutateAsync({ id: lead.id });
    navigate(`/crm/deals/${deal.public_id}`);
  }

  async function handleArchive() {
    await archive.mutateAsync(leadId);
    navigate('/crm/leads');
  }

  function handleRestore() {
    restore.mutate(leadId);
  }

  function handlePurge() {
    purge.mutate(leadId, { onSuccess: () => navigate('/crm/leads') });
  }

  if (isPending) {
    return (
      <div className="max-w-2xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/leads')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/crm/leads')}>
            <ArrowLeft />
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight">{lead.title}</h1>
          <Badge variant={statusVariant(lead.status)}>{statusLabel(lead.status)}</Badge>
        </div>
        <div className="flex gap-2">
          {lead.status !== 'converted' ? (
            // A deal needs a company — with one set, convert in a click;
            // without, the dialog picks/creates it first (DEV13-54).
            <Button
              onClick={() => (lead.company ? void handleConvert() : setConvertOpen(true))}
              disabled={convert.isPending}
            >
              <ArrowRightCircle /> {convert.isPending ? 'Converting…' : 'Convert to deal'}
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => navigate(`/crm/leads/${lead.public_id}/edit`)}>
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" aria-label="Archive lead">
                <Archive /> Archive
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archive this lead?</AlertDialogTitle>
                <AlertDialogDescription>
                  Hide this row from the active list. You can restore it later from Archived view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive} disabled={archive.isPending}>
                  {archive.isPending ? 'Archiving…' : 'Archive'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {lead.is_archived ? (
        <Card className="bg-muted/40 border-dashed">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
            <div className="text-sm">
              <p className="font-medium">This lead is archived.</p>
              <p className="text-muted-foreground">
                Restore it to make it visible in the active list again.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleRestore}
                disabled={restore.isPending}
                aria-label="Restore lead"
              >
                <RotateCcw /> {restore.isPending ? 'Restoring…' : 'Restore'}
              </Button>
              <PurgeArchivedButton
                variant="full"
                name={lead.title}
                onPurge={handlePurge}
                pending={purge.isPending}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <DetailColumns
        main={
          <>
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="Source">
                  {sourceLabel(lead.source)}
                  {lead.bid ? (
                    <span className="text-muted-foreground">
                      {' '}
                      · from bid{' '}
                      <Link
                        to={`/crm/bids/${lead.bid.public_id}`}
                        className="underline underline-offset-2"
                      >
                        {lead.bid.job_id}
                      </Link>
                    </span>
                  ) : null}
                </Field>
                <Field label="Owner">{lead.owner_name ?? '—'}</Field>
                <Field label="Company">{lead.company ? (company.data?.name ?? '…') : '—'}</Field>
                <Field label="Contact">
                  {lead.contact ? (contact.data ? contactName(contact.data) : '…') : '—'}
                </Field>
                <Field label="Created">{new Date(lead.created).toLocaleDateString()}</Field>
                <Field label="Description" className="sm:col-span-2">
                  {lead.description || '—'}
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Qualification</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <Field label="ICP fit">
                  <IcpFitBadge fit={lead.icp_fit} />
                </Field>
                <Field label="ICP segment">{lead.icp_segment_name ?? '—'}</Field>
                <Field label="Pain" className="sm:col-span-2">
                  {lead.pain || '—'}
                </Field>
                {lead.company_profile ? (
                  <CompanyProfileFields profile={lead.company_profile} />
                ) : null}
              </CardContent>
            </Card>

            <CustomFieldsPanel entity="crm.lead" entityId={lead.id} />

            <AttachmentsPanel modelLabel="crm.lead" objectId={lead.id} />

            <CommentsPanel modelLabel="crm.lead" objectId={lead.id} />
          </>
        }
        aside={
          <>
            <TagsPanel modelLabel="crm.lead" objectId={lead.id} />

            <ActivitiesPanel
              scope={{ entity: 'lead', entityId: lead.id }}
              relatedDefaults={{ company: lead.company, contact: lead.contact }}
            />

            <AuditTimelinePanel entity="crm.lead" entityId={lead.id} />
          </>
        }
      />

      <ConvertLeadDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        lead={lead}
        suggestedCompanyName={contact.data ? contactName(contact.data) : null}
        onConverted={(dealPublicId) => navigate(`/crm/deals/${dealPublicId}`)}
      />
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="mt-1 text-sm break-words whitespace-pre-wrap">{children}</div>
    </div>
  );
}
